import { FC } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { EditNoteWithQueryParamDrawer } from '../edit-note/EditNoteWithQueryParamDrawer';
import { IAction, Menu } from '../../components/Menu';
import { DeleteIcon, EditIcon, ThumbtackIcon } from '../../theme/Icons';
import { NotesModel } from '../model/NotesModel';
import { useIonAlert } from '@ionic/react';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import dayjs from 'dayjs';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';

export const ViewNoteMenu: FC<{model: NotesModel, noteId: string, trackingFrom?: string, customEditHandler?: () => void, onCloseDrawer?: () => void}> = ({ model, noteId, trackingFrom, customEditHandler, onCloseDrawer }) => {
    const [presentAlert] = useIonAlert();
    const editNoteDrawer = useInstance(EditNoteWithQueryParamDrawer);
    const history = useInstance(HistoryViewModel);
    const notesFactory = useInstance(CaremapNotesModelFactory);
    const notePinsState = notesFactory.notePinsState;
    useSubscription(() => notePinsState);

    const note = model.getNote(noteId);
    const isPinned = notePinsState.isPinned(noteId);

    if (!note) {
        return;
    }

    const actions: IAction[] = [];

    if (model.canEdit(note)) {
        actions.push({
            Icon: EditIcon,
            text: `Edit Note`,
            'data-type': 'edit-note-button',
            handler: customEditHandler || (() => editNoteDrawer.open({ noteId: note.noteId, trackingFrom }))
        });
    }

    // Pin/Unpin action (only for signed-in users who have the pin link)
    if (note.links?.pin) {
        actions.push({
            Icon: ThumbtackIcon,
            text: isPinned ? 'Unpin Note' : 'Pin Note',
            'data-type': isPinned ? 'unpin-note-button' : 'pin-note-button',
            handler: async () => {
                await notePinsState[isPinned ? 'unpinNote' : 'pinNote']({ noteId, pinLink: note.links.pin });
                notesFactory.tracking[isPinned ? 'onNoteUnpinned' : 'onNotePinned'](note, trackingFrom);
                notesFactory.toastPresenter.toast({ duration: 3000, message: isPinned ? 'Note unpinned!' : 'Note pinned!' });
                onCloseDrawer?.();
            }
        });
    }

    if (model.canDelete(note)) {
        const deleteNote = () => {
            const destructiveRole = 'destructive';
            presentAlert({
                header: `Delete Note?`,
                message: `This note will be deleted and cannot be recovered.`,
                cssClass: 'drawer-alert',
                onDidDismiss: (e) => {
                    if (e.detail.role === destructiveRole) {
                        editNoteDrawer.close();
                    }
                },
                buttons: [{
                    text: 'Delete',
                    role: destructiveRole,
                    handler: () => {
                        model.deleteNote(note);
                        history.back();
                    }
                }, {
                    text: 'Cancel',
                    role: 'cancel'
                }]
            });
        };
        actions.push({
            Icon: DeleteIcon,
            role: 'destructive',
            text: `Delete Note`,
            handler: deleteNote,
            'data-type': 'delete-button'
        });
    }

    // Add created date as informational text
    // (but only if there are already actions in the menu)
    if (note.createdAt && actions.length > 0) {
        const createdDate = dayjs(note.createdAt);
        const formattedDate = createdDate.format('MM/DD/YY [at] h:mma');
        actions.push({
            text: `Created ${formattedDate}`,
            divider: true,
            noLines: true
        });
    }

    return (
        <>
            {actions.length > 0 && (
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: -8}}>
                    <div style={{flexGrow: 1}}>&nbsp;</div>
                    <div>
                        <Menu data-id="view-note-actions" actions={actions} />
                    </div>
                </div>
            )}
        </>
    );
};
